import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import type { SearchCalendarEvent } from 'constants/searchCalendarEvents'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'

const legendMessages = defineMessages({
  oktoberfest: {
    id: 'mjrKvyAgpGTIABz57Gfl',
    defaultMessage: 'Oktoberfest ({startDate}—{endDate})',
    description: 'Legend for the Oktoberfest overlay in the hotel search calendar, with its date range in brackets',
  },
  diwali: {
    id: 'kph_D3LtzavUweJFoS9a',
    defaultMessage: 'Diwali ({startDate}—{endDate})',
    description: 'Legend for the Diwali overlay in the hotel search calendar, with its date range in brackets',
  },
  sydney_nye: {
    id: 'VFZJBhzOPPWSWmi2oZRE',
    defaultMessage: 'Sydney New Year’s Eve ({startDate}—{endDate})',
    description: 'Legend for the Sydney New Year’s Eve overlay in the hotel search calendar, with its date range in brackets',
  },
  lunar_new_year: {
    id: 'Z2cFi99LzoNxuj0EHLrV',
    defaultMessage: 'Lunar New Year ({startDate}—{endDate})',
    description: 'Legend for the Lunar New Year overlay in the hotel search calendar, with its date range in brackets',
  },
  rio_carnival: {
    id: 'a0M1I5XMC39fWx_Qvghn',
    defaultMessage: 'Rio Carnival ({startDate}—{endDate})',
    description: 'Legend for the Rio Carnival overlay in the hotel search calendar, with its date range in brackets',
  },
  mardi_gras: {
    id: 'V7LXxYyfobUpilCcVo3A',
    defaultMessage: 'Mardi Gras ({startDate}—{endDate})',
    description: 'Legend for the Mardi Gras overlay in the hotel search calendar, with its date range in brackets',
  },
  holi: {
    id: 'sZ5kRaEPGbHDCn2bNgyt',
    defaultMessage: 'Holi ({startDate}—{endDate})',
    description: 'Legend for the Holi overlay in the hotel search calendar, with its date range in brackets',
  },
  cherry_blossom: {
    id: 'SLPy8Uq5d-zVadh4kUqV',
    defaultMessage: 'Cherry blossom season ({startDate}—{endDate})',
    description: 'Legend for the cherry blossom overlay in the hotel search calendar, with its viewing date range in brackets',
  },
  songkran: {
    id: 'gXZOqyakHJK_txWofvvT',
    defaultMessage: 'Songkran ({startDate}—{endDate})',
    description: 'Legend for the Songkran overlay in the hotel search calendar, with its date range in brackets',
  },
  san_fermin: {
    id: 'BOwyj2kqRzjGDRKJ5tns',
    defaultMessage: 'San Fermín ({startDate}—{endDate})',
    description: 'Legend for the San Fermín overlay in the hotel search calendar, with its date range in brackets',
  },
  indonesia_independence_day: {
    id: 'grvKoiKmcGjNQ2GYma4B',
    defaultMessage: 'Indonesian Independence Day ({startDate})',
    description: 'Legend for the Indonesian Independence Day overlay in the hotel search calendar, with its date in brackets',
  },
})

const tooltipMessages = defineMessages({
  oktoberfest: {
    id: 'W3VJVj0otoBkb5MxzO4A',
    defaultMessage: 'Munich’s beer festival, with tents, music and traditional Bavarian dress.',
    description: 'Hover tooltip describing Oktoberfest on the hotel search calendar event legend',
  },
  diwali: {
    id: 'UKKcWoCCCLiF-GcaKGqy',
    defaultMessage: 'India’s festival of lights, marked by lamps, sweets and fireworks.',
    description: 'Hover tooltip describing Diwali on the hotel search calendar event legend chip',
  },
  sydney_nye: {
    id: 'A1NVEiCNGXeMUzS8B0DE',
    defaultMessage: 'Sydney Harbour fireworks, one of the world’s most watched New Year’s Eve nights.',
    description: 'Hover tooltip describing Sydney New Year’s Eve on the hotel search calendar legend',
  },
  lunar_new_year: {
    id: 'XyM-rlhC7vl_0K9gjsV5',
    defaultMessage: 'Lunar New Year celebrations across East and Southeast Asia, with family gatherings and lanterns.',
    description: 'Hover tooltip describing Lunar New Year on the hotel search calendar event legend',
  },
  rio_carnival: {
    id: '1vI8J4bFUBisaJ0YQxmG',
    defaultMessage: 'Rio’s street carnival, with samba parades, music and elaborate costumes.',
    description: 'Hover tooltip describing Rio Carnival on the hotel search calendar event legend',
  },
  mardi_gras: {
    id: 'V_jAuS1_cdoY5ALDZOog',
    defaultMessage: 'New Orleans’ carnival, with parades, masks and colourful beads.',
    description: 'Hover tooltip describing Mardi Gras on the hotel search calendar event legend chip',
  },
  holi: {
    id: '3ktZl3U46JhYVmQlnvsG',
    defaultMessage: 'India’s festival of colour, where people throw powdered dyes to welcome spring.',
    description: 'Hover tooltip describing Holi on the hotel search calendar event legend chip',
  },
  cherry_blossom: {
    id: 'cfx6GxcCofa6W1ShU6MS',
    defaultMessage: 'Japan’s sakura season, when parks and temples fill with pale pink blooms.',
    description: 'Hover tooltip describing cherry blossom season on the hotel search calendar legend',
  },
  songkran: {
    id: 'atMMwAVYnR0JAioZYi6u',
    defaultMessage: 'Thailand’s New Year water festival, with street splashing and temple visits.',
    description: 'Hover tooltip describing Songkran on the hotel search calendar event legend chip',
  },
  san_fermin: {
    id: 'y0-dQBFzw_E3ZDFSPnHa',
    defaultMessage: 'Pamplona’s festival, best known for the running of the bulls.',
    description: 'Hover tooltip describing San Fermín on the hotel search calendar event legend chip',
  },
  indonesia_independence_day: {
    id: 'zhEJKMsh_LKhYy6EG2QG',
    defaultMessage: 'Indonesia’s national day, marked by flag ceremonies, parades and village games.',
    description: 'Hover tooltip describing Indonesian Independence Day on the hotel search calendar legend',
  },
})

const fallbackMessages = defineMessages({
  label: {
    id: '_6ZQAJTGnNP2P_c0GJRy',
    defaultMessage: '{title} ({startDate}—{endDate})',
    description: 'Legend for a hotel search calendar event that has no hand-written label, wrapping the event title with its bracketed date range',
  },
  singleDayLabel: {
    id: 'urOgnrogVUkcLS8wioqA',
    defaultMessage: '{title} ({startDate})',
    description: 'Legend for a single-day hotel search calendar event that has no hand-written label, wrapping the event title with its bracketed date',
  },
})

function formatLegendDate(isoDate: string, locale: string) {
  return intlDateFormatter(isoDate, INTL_DAY_MONTH_NAME_SHORT, locale)
}

function useSearchCalendarEventNames(events: ReadonlyArray<SearchCalendarEvent>) {
  const intl = useIntl()

  return useMemo(() => {
    return Object.fromEntries(events.map(event => {
      const startDate = formatLegendDate(event.startDate, intl.locale)
      const endDate = formatLegendDate(event.endDate, intl.locale)
      const message = legendMessages[event.id as keyof typeof legendMessages]
      const tooltip = tooltipMessages[event.id as keyof typeof tooltipMessages]

      // Rows sourced from the events feed carry their own copy rather than a
      // hand-written message. The date wrapper stays localised either way; only
      // the title and description come through untranslated.
      const label = message ?
          intl.formatMessage(message, { startDate, endDate }) :
          intl.formatMessage(
            event.startDate === event.endDate ? fallbackMessages.singleDayLabel : fallbackMessages.label,
            { title: event.title, startDate, endDate },
          )

      return [event.id, {
        label,
        tooltip: tooltip ? intl.formatMessage(tooltip) : event.shortDescription ?? '',
      }]
    })) as Record<string, { label: string, tooltip: string } | undefined>
  }, [events, intl])
}

export default useSearchCalendarEventNames
